// src/scenes/Register/index.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import {
  Box, Button, TextField, Typography, Avatar, Paper, Grid,
  FormControl, InputLabel, Select, MenuItem, Alert
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyRole, setCompanyRole] = useState('');
  const [orgName, setOrgName] = useState('');
  const [numEmployees, setNumEmployees] = useState('');
  const [hearAboutUs, setHearAboutUs] = useState('');
  const [hearAboutUsOther, setHearAboutUsOther] = useState('');
  const [error, setError] = useState(null);
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setOk('');

    if (password !== confirm) return setError('Passwords do not match.');
    if (!orgName || !numEmployees || !hearAboutUs) {
      return setError('Please fill in all organization fields.');
    }
    if (hearAboutUs === 'Other' && !hearAboutUsOther) {
      return setError('Please specify how you heard about us.');
    }

    setLoading(true);
    const hearAboutValue = hearAboutUs === 'Other' ? hearAboutUsOther : hearAboutUs;

    const { data, error: signupError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: `${firstName} ${lastName}`.trim(),
          phone,
          company_role: companyRole,
          org_name: orgName,         // used by trigger to name the org
          num_employees: numEmployees,
          hear_about_us: hearAboutValue,
        },
        emailRedirectTo: window.location.origin + '/login'
      }
    });

    setLoading(false);

    if (signupError) {
      setError(signupError.message);
      return;
    }

    // If email confirmation is on, the user must confirm before logging in.
    setOk('Check your email to confirm your account, then log in.');
    setTimeout(() => navigate('/login', { replace: true }), 1200);
  };

  return (
    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '100vh', p: 2 }}>
      <Grid item xs={12} sm={10} md={5}>
        <Paper elevation={6} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">Register</Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}

              <TextField required fullWidth label="First Name" margin="normal"
                value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <TextField required fullWidth label="Last Name" margin="normal"
                value={lastName} onChange={(e) => setLastName(e.target.value)} />
              <TextField required fullWidth label="Email Address" type="email" margin="normal"
                value={email} onChange={(e) => setEmail(e.target.value)} />
              <TextField fullWidth label="Phone Number" type="tel" margin="normal"
                value={phone} onChange={(e) => setPhone(e.target.value)} />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="company-role-label">Company Role</InputLabel>
                <Select labelId="company-role-label" label="Company Role"
                  value={companyRole} onChange={(e) => setCompanyRole(e.target.value)}>
                  <MenuItem value="CEO">CEO</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                  <MenuItem value="Employee">Employee</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField required fullWidth label="Organization Name" margin="normal"
                value={orgName} onChange={(e) => setOrgName(e.target.value)} />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="num-employees-label">Number of Employees</InputLabel>
                <Select labelId="num-employees-label" label="Number of Employees"
                  value={numEmployees} onChange={(e) => setNumEmployees(e.target.value)}>
                  <MenuItem value="1-10">1-10</MenuItem>
                  <MenuItem value="11-50">11-50</MenuItem>
                  <MenuItem value="51-200">51-200</MenuItem>
                  <MenuItem value="201-1000">201-1000</MenuItem>
                  <MenuItem value="1000+">1000+</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="hear-about-us-label">How did you hear about us?</InputLabel>
                <Select labelId="hear-about-us-label" label="How did you hear about us?"
                  value={hearAboutUs}
                  onChange={(e) => {
                    setHearAboutUs(e.target.value);
                    if (e.target.value !== 'Other') setHearAboutUsOther('');
                  }}>
                  <MenuItem value="Search Engine">Search Engine</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              {hearAboutUs === 'Other' && (
                <TextField required fullWidth label="Please specify" margin="normal"
                  value={hearAboutUsOther} onChange={(e) => setHearAboutUsOther(e.target.value)} />
              )}

              <TextField required fullWidth label="Password" type="password" margin="normal"
                value={password} onChange={(e) => setPassword(e.target.value)} />
              <TextField required fullWidth label="Confirm Password" type="password" margin="normal"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />

              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={loading}>
                {loading ? 'Signing up…' : 'Sign Up'}
              </Button>
              <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={() => navigate('/login')}>
                Already have an account? Log in
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}
